import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Blocker, Link, useBlocker, useNavigate, useParams } from 'react-router-dom';

import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	ArrowLeftOutline,
	Button,
	Display,
	HStack,
	ListItem,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spacer,
	Stack,
	Text,
	UnorderedList,
	useDisclosure,
	useToast,
} from '@bonusly/design-system';
import { assertPresence } from '@bonusly/runtime';
import { useAnalytics } from '@bonusly/who-am-i';

import { Reveal } from '../../../components/display/reveal';
import { PageTitle } from '../../../components/page/page-title';
import { FORM_PANEL_WIDTH } from '../../../config/admin';
import { GroupType } from '../../../entities/groups';
import { Incentive, IncentiveState } from '../../../entities/incentives';
import { Schema } from '../../../lib/schema';
import { useIncentive, useUpdateIncentive } from '../../../queries/incentives';
import { ClaimLimitMode, IncentiveFormData } from '../types';
import { DeactivateIncentiveModal } from './deactivate-incentive-modal';
import { DeleteIncentiveModal } from './delete-incentive-modal';
import { AdminIncentiveTrackingEvent } from './events';
import { IncentiveForm } from './incentive-form';
import { useSchema } from './use-schema';
import { buildFormBody } from './utils';

export const EditIncentive: FC = () => {
	const { t } = useTranslation('incentives', { keyPrefix: 'edit' });
	const { track } = useAnalytics();
	const { id } = useParams<{ id: string }>();
	const toast = useToast();
	const navigate = useNavigate();
	const confirmModal = useDisclosure();
	const deactivateModal = useDisclosure();
	const deleteModal = useDisclosure();
	const reactivateModal = useDisclosure();
	const cancelRef = useRef<HTMLButtonElement>(null);
	const schema = useSchema();

	assertPresence(id);

	const { data: record } = useIncentive(id);
	assertPresence(record);

	const form = useForm<IncentiveFormData>({
		defaultValues: {
			name: record.name,
			amount: record.amount ?? 0,
			description: record.description,
			requireProof: record.requireProof,
			icon: record.iconUrl,
			message: record.message,
			conditionsOperator: record.conditionsOperator ?? GroupType.Or,
			conditions: Schema.serialize<{ attr: string; value: string }[]>(record.conditions ?? []),
			claimLimitMode: (() => {
				if (!record.claimLimit && !record.quantity) {
					return ClaimLimitMode.Unlimited;
				}

				if (!!record.quantity) {
					return ClaimLimitMode.PerPeriod;
				}

				return ClaimLimitMode.PerEmployee;
			})(),
			claimLimitQuantity: record.quantity,
			periodType: record.periodType,
			userClaimLimit: record.claimLimit,
			visibility: record.visibility,
			requireApproval: record.approvalLevels.length > 0,
			approvalLevels: structuredClone(record.approvalLevels),
			state: record.state,
		},
		resolver: zodResolver(schema),
	});

	const { mutate, isLoading } = useUpdateIncentive(record, {
		onMutate: () => {},
		onSuccess: () => {
			toast({
				title: record.isActive ? t('successToast') : t('reactivatedToast', { name: record.name }),
				status: 'success',
			});
			navigate('/admin/programs/incentives');
		},
		onError: () => {
			toast({
				title: t('errorToast'),
				status: 'error',
			});
		},
	});

	const blocker = useBlocker(({ currentLocation, nextLocation }) => {
		return !isLoading && form.formState.isDirty && currentLocation !== nextLocation;
	});

	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (form.formState.isDirty) {
				e.preventDefault();
				e.returnValue = '';
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [form]);

	const handleConfirmUpdate = () => {
		confirmModal.onOpen();
	};

	const handleSubmitForm = () => {
		const currentValues = form.getValues();
		mutate(buildFormBody(currentValues));
	};

	const handleReactivateForm = () => {
		const currentValues = form.getValues();
		const updateData = buildFormBody(currentValues);
		mutate({ ...updateData, state: IncentiveState.Active });
		track(AdminIncentiveTrackingEvent.ReactivateIncentive, { incentiveId: record.id });
	};

	return (
		<>
			<PageTitle>{t('title')}</PageTitle>

			<Stack px={10} py={8} spacing={6} maxW={FORM_PANEL_WIDTH}>
				<HStack>
					<Button as={Link} variant="link" gap={1} to="/admin/programs/incentives" type="button">
						<ArrowLeftOutline size="xs" />
						{t('back')}
					</Button>
				</HStack>

				<Display size="sm">{t('displayName', { name: record.name })}</Display>

				<IncentiveForm form={form} isLoading={isLoading} onSubmit={handleConfirmUpdate}>
					<HStack justify="space-between" width="100%">
						<Reveal if={record.isActive}>
							<Button variant="alpha" colorScheme="secondary" onClick={deactivateModal.onOpen}>
								{t('deactivate')}
							</Button>
							<Button type="submit" isLoading={isLoading} isDisabled={!form.formState.isDirty}>
								{t('saveChanges')}
							</Button>
						</Reveal>

						<Reveal if={!record.isActive}>
							<Button variant="alpha" colorScheme="secondary" onClick={deleteModal.onOpen}>
								{t('delete')}
							</Button>
							<Button onClick={reactivateModal.onOpen} isLoading={isLoading}>
								{t('reactivate')}
							</Button>
						</Reveal>
					</HStack>
				</IncentiveForm>
			</Stack>

			<ConfirmUpdateModal form={form} isLoading={isLoading} isOpen={confirmModal.isOpen} onClose={confirmModal.onClose} onConfirm={handleSubmitForm} />
			<DeactivateIncentiveModal
				incentive={record}
				isOpen={deactivateModal.isOpen}
				onClose={deactivateModal.onClose}
				onSuccess={() => navigate('/admin/programs/incentives')}
			/>
			<UnsavedChangesModal blocker={blocker} cancelRef={cancelRef} />
			<DeleteIncentiveModal
				incentive={record}
				isOpen={deleteModal.isOpen}
				onClose={deleteModal.onClose}
				onSuccess={() => navigate('/admin/programs/incentives')}
			/>
			<ReactivateIncentiveModal
				incentive={record}
				isOpen={reactivateModal.isOpen}
				onClose={reactivateModal.onClose}
				form={form}
				onConfirm={handleReactivateForm}
				isLoading={isLoading}
			/>
		</>
	);
};

type ConfirmUpdateModalProps = {
	form: ReturnType<typeof useForm<IncentiveFormData>>;
	isLoading: boolean;
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
};

type FieldSet = keyof IncentiveFormData;

const fieldMap: Partial<Record<FieldSet, string>> = {
	name: 'confirmName',
	amount: 'confirmAmount',
	description: 'confirmDescription',
	requireProof: 'confirmProof',
	icon: 'confirmImage',
	message: 'confirmMessage',
	conditionsOperator: 'confirmConditionsOperator',
	conditions: 'confirmConditions',
	claimLimitMode: 'confirmClaimLimitMode',
	periodType: 'confirmPeriodType',
	userClaimLimit: 'confirmUserClaimLimit',
	visibility: 'confirmVisibility',
	approvalLevels: 'confirmApprovalLevels',
};

export const ConfirmUpdateModal: FC<ConfirmUpdateModalProps> = ({ form, isLoading, isOpen, onClose, onConfirm }) => {
	const { t } = useTranslation('incentives', { keyPrefix: 'edit' });
	const edited = Object.entries(fieldMap)
		.filter(([key]) => !!form.formState.dirtyFields[key as FieldSet])
		.map(([, key]) => key);

	const keys = [...new Set(edited)];

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="lg">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>{t('confirmModal.header')}</ModalHeader>
				<ModalCloseButton />
				<ModalBody p={5}>
					<Text mb={2}>{t('confirmModal.description')}</Text>
					<UnorderedList pl={2}>
						{keys.map((key, i) => (
							<ListItem key={i}>{t(key)}</ListItem>
						))}
					</UnorderedList>
					<Text mt={2}>{t('confirmModal.description2')}</Text>
				</ModalBody>

				<ModalFooter>
					<Button variant="alpha" colorScheme="secondary" mr={2} onClick={onClose}>
						{t('confirmModal.cancel')}
					</Button>
					<Spacer />
					<Button isLoading={isLoading} onClick={onConfirm}>
						{t('confirmModal.confirm')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

type ReactivateIncentiveModalProps = {
	incentive: Incentive;
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	redirectPath?: string;
	form?: ReturnType<typeof useForm<IncentiveFormData>>;
	onConfirm: () => void;
	isLoading: boolean;
};

const ReactivateIncentiveModal: FC<ReactivateIncentiveModalProps> = ({ incentive, isOpen, onClose, form, onConfirm, isLoading }) => {
	const { t } = useTranslation('incentives', { keyPrefix: 'reactivate' });

	const edited = form
		? Object.entries(fieldMap)
				.filter(([key]) => !!form.formState.dirtyFields[key as FieldSet])
				.map(([, key]) => key)
		: [];

	const hasChanges = edited.length > 0;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="lg">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>{t('title')}</ModalHeader>
				<ModalCloseButton />
				<ModalBody p={5}>
					<Stack spacing={4}>
						<Text fontWeight="semibold">{t('confirmationMessage', { incentiveName: incentive.name })}</Text>
						<Reveal if={hasChanges}>
							<Text>{t('description')}</Text>
							<UnorderedList pl={2}>
								{edited.map((key, i) => (
									<ListItem key={i}>{key}</ListItem>
								))}
							</UnorderedList>
						</Reveal>
						<Text>{t('description2')}</Text>
					</Stack>
				</ModalBody>

				<ModalFooter>
					<Button variant="alpha" colorScheme="secondary" mr={2} onClick={onClose}>
						{t('cancelButtonLabel')}
					</Button>
					<Spacer />
					<Button colorScheme="primary" variant="solid" isLoading={isLoading} onClick={onConfirm}>
						{t('reactivateButtonLabel')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

const UnsavedChangesModal: FC<{
	blocker: Blocker;
	cancelRef: React.RefObject<HTMLButtonElement>;
}> = ({ blocker, cancelRef }) => {
	const { t } = useTranslation('incentives', { keyPrefix: 'edit' });

	return (
		<AlertDialog leastDestructiveRef={cancelRef} isOpen={blocker.state === 'blocked'} onClose={() => blocker.reset?.()}>
			<AlertDialogOverlay>
				<AlertDialogContent>
					<AlertDialogHeader fontSize="lg" fontWeight="bold">
						{t('unsavedChanges.header')}
					</AlertDialogHeader>
					<AlertDialogCloseButton />

					<AlertDialogBody>{t('unsavedChanges.description')}</AlertDialogBody>

					<AlertDialogFooter>
						<Button colorScheme="secondary" variant="alpha" ref={cancelRef} onClick={() => blocker.reset?.()}>
							{t('unsavedChanges.continueEditing')}
						</Button>
						<Spacer />
						<Button colorScheme="primary" variant="solid" onClick={() => blocker.proceed?.()}>
							{t('unsavedChanges.discardChanges')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialogOverlay>
		</AlertDialog>
	);
};
